import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { EASE, fadeUp } from "../../lib/motion";
import { resolveImg } from "../../lib/image";
import { useModal } from "../../lib/ModalContext";
import { AnimatedNumber } from "./AnimatedNumber";

export const Hero = ({ content }) => {
    const h = content?.hero || {};
    const ref = useRef(null);
    const { open } = useModal();
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, -60]);

    return (
        <section id="top" ref={ref} className="relative pt-28 md:pt-32 lg:pt-36 pb-6 md:pb-20" data-testid="hero-section">
            <div className="mx-auto max-w-[1240px] px-6 md:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-end">
                    {/* LEFT */}
                    <div className="lg:col-span-7">
                        <motion.span
                            {...fadeUp(0)}
                            className="inline-flex items-center gap-2 rounded-full border border-oth-line/80 bg-white/60 px-3.5 py-1.5 text-xs uppercase tracking-[0.22em] text-oth-ink2 font-body"
                            data-testid="hero-pill"
                        >
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#0B3B60" }} />
                            {h.pill}
                        </motion.span>

                        <h1
                            className="mt-7 font-display font-semibold text-[44px] sm:text-[58px] md:text-[78px] lg:text-[92px] leading-[0.96] tracking-[-0.04em]"
                            style={{ color: (h.headline_color || "").trim() || "#0A0A0A" }}
                            data-testid="hero-headline"
                        >
                            {(h.headline_lines || []).map((line, i) => {
                                const text = typeof line === "string" ? line : (line?.text ?? "");
                                const perLine = typeof line === "object" ? (line?.color || "").trim() : "";
                                return (
                                    <motion.span
                                        key={`hl-${i}-${text}`}
                                        initial={{ opacity: 0, y: 40, filter: "blur(14px)" }}
                                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                                        transition={{ duration: 1.15, ease: EASE, delay: 0.25 + i * 0.16 }}
                                        className="block"
                                        style={perLine ? { color: perLine } : undefined}
                                    >
                                        {text}
                                    </motion.span>
                                );
                            })}
                        </h1>

                        <motion.p
                            {...fadeUp(0.7)}
                            className="mt-8 max-w-[520px] font-body text-base md:text-lg text-oth-ink2 leading-relaxed"
                            data-testid="hero-paragraph"
                        >
                            {h.paragraph}
                        </motion.p>

                        <div className="mt-10 grid grid-cols-2 gap-4 max-w-[520px]">
                            {(h.stats || []).map((s, i) => (
                                <motion.div
                                    key={`stat-${s.label || i}`}
                                    {...fadeUp(0.85 + i * 0.1)}
                                    className="rounded-3xl border border-oth-line/70 bg-white/50 backdrop-blur-sm p-5 md:p-6"
                                    data-testid={`hero-stat-${i}`}
                                >
                                    <div className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
                                        <AnimatedNumber
                                            value={s.value}
                                            seconds={h.counter_seconds}
                                            testId={`hero-stat-${i}-value`}
                                        />
                                    </div>
                                    <div className="mt-2 font-body text-xs md:text-sm text-oth-ink3 leading-snug">{s.label}</div>
                                </motion.div>
                            ))}
                        </div>

                        <motion.div {...fadeUp(1.05)} className="mt-10 text-center md:text-left">
                            <button
                                type="button"
                                onClick={() => open("book")}
                                className="btn-pill btn-pill-dark"
                                data-testid="hero-cta"
                            >
                                {h.cta_label || "Book a private demo"}
                                <ArrowUpRight size={16} />
                            </button>
                        </motion.div>
                    </div>

                    {/* RIGHT */}
                    <div className="lg:col-span-5">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.96, filter: "blur(12px)" }}
                            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                            transition={{ duration: 1.2, ease: EASE, delay: 0.35 }}
                            style={{ y: imgY }}
                            className="relative rounded-[36px] overflow-hidden aspect-[4/5] surface-card"
                            data-testid="hero-image-frame"
                        >
                            <img
                                src={resolveImg(h.image_url)}
                                alt="Family"
                                className="absolute inset-0 w-full h-full object-cover slow-zoom"
                                data-testid="hero-image"
                            />
                            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.25) 100%)" }} />
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};
